import type { DehydratedState } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type {
  PersistedClient,
  Persister,
  PersistQueryClientProviderProps,
} from '@tanstack/react-query-persist-client';
import { queryClient } from '@shared/lib/react-query';

export const QUERY_PERSIST_KEY = 'jema_rq_cache_v1';
export const QUERY_PERSIST_BUSTER = 'v2';
export const QUERY_PERSIST_MAX_AGE = 10 * 60 * 1000;

const PERSISTABLE_ROOT_KEYS = new Set([
  'user',
  'procedures',
  'specialties',
  'doctor-quotes',
  'notifications',
  'patient-home',
  'files',
]);

const NON_PERSISTABLE_ROOT_KEYS = new Set(['payments', 'patient-quotes']);

const isPersistableQuery = (queryKey: readonly unknown[]) => {
  const root = typeof queryKey[0] === 'string' ? queryKey[0] : null;
  if (!root) return false;
  if (NON_PERSISTABLE_ROOT_KEYS.has(root)) return false;
  return PERSISTABLE_ROOT_KEYS.has(root);
};

const noopPersister: Persister = {
  persistClient: async (_client: PersistedClient) => {},
  restoreClient: async () => undefined,
  removeClient: async () => {},
};

const isBrowser = typeof window !== 'undefined';

export const queryPersister: Persister = isBrowser
  ? createSyncStoragePersister({
      key: QUERY_PERSIST_KEY,
      storage: window.localStorage,
    })
  : noopPersister;

export const queryPersistOptions: PersistQueryClientProviderProps['persistOptions'] = {
  persister: queryPersister,
  buster: QUERY_PERSIST_BUSTER,
  maxAge: QUERY_PERSIST_MAX_AGE,
  dehydrateOptions: {
    shouldDehydrateQuery: query => {
      if (query.state.status !== 'success') return false;
      return isPersistableQuery(query.queryKey);
    },
    shouldDehydrateMutation: () => false,
  },
};

export const shouldRestorePersistedCache = (restored: DehydratedState | undefined) => {
  return Boolean(restored);
};

export { queryClient };
